import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { logError } from "../lib/logError";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Clock } from "@phosphor-icons/react";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
const ACTIVITY_KEY = "claro_session_activity"; // cross-tab activity broadcast

// Inactivity session timeout: after `idle` minutes of no activity, warn for
// `warning` minutes with a live countdown, then log the user out (reason
// "timeout"). Durations are tenant-configurable (Admin → Security).
export function SessionGuard() {
  const { user, logout } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const idleTimer = useRef(null);
  const tickTimer = useRef(null);
  const warnOpenRef = useRef(false);
  const loggingOut = useRef(false);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const armRef = useRef(() => {});
  const timeoutRef = useRef(() => {});

  useEffect(() => {
    if (!user) { setCfg(null); return undefined; }
    const fetchCfg = () => api.get("/auth/session-config").then((r) => setCfg(r.data)).catch(() => setCfg({ idle_minutes: 60, warning_minutes: 5 }));
    fetchCfg();
    const onChange = () => fetchCfg();  // admin saved a new timeout → apply live
    window.addEventListener("session-config-changed", onChange);
    return () => window.removeEventListener("session-config-changed", onChange);
  }, [user]);

  useEffect(() => {
    if (!user || !cfg) return undefined;
    loggingOut.current = false;
    const idleMs = Math.max(1, (cfg.idle_minutes || 60) - (cfg.warning_minutes || 5)) * 60 * 1000;
    const warnMs = (cfg.warning_minutes || 5) * 60 * 1000;
    const totalMs = idleMs + warnMs;
    // Wall-clock based: the logout is decided from the REAL time since last activity,
    // not from a setTimeout duration. This is what makes it survive a sleeping screen /
    // throttled background tab — elapsed idle time keeps counting, and returning to the
    // window re-evaluates it instead of resetting the clock.
    const initial = (() => { try { return Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now(); } catch (e) { return Date.now(); } })();
    const lastActivity = { t: initial };

    const doTimeout = () => {
      if (loggingOut.current) return;
      loggingOut.current = true;
      clearTimeout(idleTimer.current);
      clearInterval(tickTimer.current);
      warnOpenRef.current = false;
      setWarnOpen(false);
      logoutRef.current("timeout");
    };
    const startWarning = () => {
      const elapsed = Date.now() - lastActivity.t;
      if (elapsed >= totalMs) { doTimeout(); return; }
      warnOpenRef.current = true;
      setRemaining(Math.ceil((totalMs - elapsed) / 1000));
      setWarnOpen(true);
      clearInterval(tickTimer.current);
      tickTimer.current = setInterval(() => {
        const rem = Math.ceil((totalMs - (Date.now() - lastActivity.t)) / 1000);
        if (rem <= 0) { doTimeout(); return; }
        setRemaining(rem);
      }, 1000);
    };
    // Re-evaluate against the wall clock (called on real activity AND on returning to
    // the window). Never blindly resets unless there was genuine activity.
    const evaluate = () => {
      if (warnOpenRef.current) return;
      const elapsed = Date.now() - lastActivity.t;
      if (elapsed >= totalMs) { doTimeout(); return; }
      clearTimeout(idleTimer.current);
      if (elapsed >= idleMs) { startWarning(); return; }
      idleTimer.current = setTimeout(startWarning, idleMs - elapsed);
    };
    const arm = () => {                       // genuine activity → reset the clock
      lastActivity.t = Date.now();
      warnOpenRef.current = false;
      setWarnOpen(false);
      clearInterval(tickTimer.current);
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(startWarning, idleMs);
    };
    let lastBroadcast = 0;
    const broadcast = () => {
      const now = Date.now();
      if (now - lastBroadcast > 2000) {  // throttle cross-tab writes
        lastBroadcast = now;
        try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch (e) { logError("session cross-tab broadcast", e); }
      }
    };
    // Tell the SERVER about genuine activity (throttled ~60s) so the backend idle
    // window tracks real user activity, not just API calls. Without this, a user
    // actively reading a page (mouse/scroll but no API request) would be reset
    // client-side yet falsely expired server-side. Seeded to "now" (login = activity).
    let lastServerPing = Date.now();
    const pingServer = () => {
      const now = Date.now();
      if (now - lastServerPing > 60000) {
        lastServerPing = now;
        api.post("/auth/session-heartbeat").catch(() => {});
      }
    };
    armRef.current = () => { arm(); broadcast(); pingServer(); };
    timeoutRef.current = doTimeout;
    evaluate();  // account for time already elapsed since the stored last-activity
    // Real user activity in THIS tab → reset. Suppressed while the warning shows so a
    // stray event can't silently cancel it (only the dialog buttons act).
    const onActivity = () => { if (!warnOpenRef.current) { arm(); broadcast(); pingServer(); } };
    // Another tab reported activity → adopt its timestamp, then re-evaluate.
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY && !warnOpenRef.current) {
        const t = Number(e.newValue) || 0;
        if (t > lastActivity.t) lastActivity.t = t;
        evaluate();
      }
    };
    // Returning to the window is NOT activity — re-evaluate elapsed idle time (may log
    // out immediately if the screen was asleep past the timeout).
    const onVisible = () => { if (document.visibilityState === "visible") evaluate(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      clearTimeout(idleTimer.current);
      clearInterval(tickTimer.current);
    };
  }, [user, cfg]);

  if (!user || !warnOpen) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <Dialog open={warnOpen} onOpenChange={() => {}}>
      <DialogContent className="rounded-none max-w-md" data-testid="session-warning-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock size={20} className="text-amber-600" />Session about to expire</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">You've been inactive. For security, you'll be signed out in:</p>
        <div data-testid="session-countdown" className="font-display font-black text-4xl text-center my-3 tabular-nums">{mm}:{ss}</div>
        <div className="flex justify-end gap-2">
          <Button data-testid="session-logout-now-btn" variant="outline" className="rounded-none" onClick={() => timeoutRef.current()}>Sign out now</Button>
          <Button data-testid="session-stay-btn" className="rounded-none bg-primary hover:bg-primary/90" onClick={() => armRef.current()}>Stay signed in</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Shown over the login page after an inactivity timeout. Either button just
// dismisses the popup, revealing the login page beneath.
export function SessionTimeoutModal() {
  const { timedOut, dismissTimeout } = useAuth();
  if (!timedOut) return null;
  return (
    <Dialog open={timedOut} onOpenChange={dismissTimeout}>
      <DialogContent className="rounded-none max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock size={20} className="text-destructive" />Session timed out</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Your session ended due to inactivity. Please sign in again to continue.</p>
        <div className="flex justify-end gap-2 mt-2">
          <Button data-testid="session-timeout-close-btn" variant="outline" className="rounded-none" onClick={dismissTimeout}>Close</Button>
          <Button data-testid="session-timeout-login-btn" className="rounded-none bg-primary hover:bg-primary/90" onClick={dismissTimeout}>Log in</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
